import { Box, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DotLegalButton, DotLegalCheckbox, DotLegalFullScreenSpinner, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import DotLegalPaper from "@common/components/dotLegalPaper/DotLegalPaper";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import DotLegalDeleteDialogWrapper from "@common/components/dotLegalDeleteDialog/DotLegalDeleteDialogWrapper";
import { useTranslation } from "@localization/useTranslation";
import { addSectionIcon, importIcon, publishIcon, reorderSectionsIcon, showDiffIcon, trashcan } from "@common/icons";
import { Link } from "react-router";
import PolicySectionRow from "./PolicySectionRow";
import PolicySectionDragClone from "./PolicySectionDragClone";
import PolicyVersionDetails from "./PolicyVersionDetails";
import { policyDescriptionEditId, usePolicySectionsEditor } from "./PolicySections.hooks";
import { usePolicySectionEditorStyles } from "./PolicySectionsEditor.styles.ts";
import { PolicyVersionSummary } from "../policyCompareVersions/PolicyCompareVersions.types";
import { PolicyVersionStatus } from "./PolicySections.types";
import { usePolicyQuery } from "../policyOverview/PolicyOverview.hooks";
import { useUrlProvider } from "../../useUrlProvider";

export interface PolicySectionsEditorProps {
    policyId: string;
    versionId?: string;
    canPublish: boolean;
    canDiscard: boolean;
    onPublishClick: () => void;
    onDiscardClick: () => void;
    isDiscardingDraft: boolean;
    canStartDraft: boolean;
    onStartDraftClick: () => void;
    isStartingDraft: boolean;
    canImport: boolean;
    onImportClick: () => void;
}
export const sectionOuterPaperPadding = 7.5;
function PolicySectionsEditor({
    policyId,
    versionId,
    canPublish,
    canDiscard,
    onPublishClick,
    onDiscardClick,
    isDiscardingDraft,
    canStartDraft,
    onStartDraftClick,
    isStartingDraft,
    canImport,
    onImportClick,
}: PolicySectionsEditorProps) {
    const { translateString } = useTranslation();
    const { getPolicyCompareUrl } = useUrlProvider();
    const editor = usePolicySectionsEditor(policyId, versionId);
    const styles = usePolicySectionEditorStyles();
    const policyQuery = usePolicyQuery(policyId);
    const versions = policyQuery.data?.versions ?? [];
    const canCompare = versions.length >= 2;
    // A draft shares the current version's major.minor until publish, so break ties by status
    // to keep the draft last — otherwise compare opens against the approved version of the same number.
    const draftRank = (version: PolicyVersionSummary) => (version.status === PolicyVersionStatus.Draft ? 1 : 0);
    const sortedVersions = [...versions].sort((a, b) => a.major - b.major || a.minor - b.minor || draftRank(a) - draftRank(b));
    const compareUrl = canCompare
        ? getPolicyCompareUrl(policyId, sortedVersions[sortedVersions.length - 2].versionId, sortedVersions[sortedVersions.length - 1].versionId)
        : undefined;

    if (editor.isLoading) {
        return (
            <Box sx={{ height: "60vh", width: "100%" }}>
                <DotLegalFullScreenSpinner />
            </Box>
        );
    }

    return (
        <Box sx={(theme) => ({ display: "flex", justifyContent: "center", mt: 2 })}>
            {/* width 100% is for dragNDrop of sections */}
            <Box sx={{ maxWidth: "1300px", width: "100%" }}>
                <DotLegalPaper
                    sx={(theme) => ({
                        paddingBottom: 0,
                        paddingX: sectionOuterPaperPadding,
                        paddingTop: 3,
                        color: theme.palette.primary.dark,
                        "& .MuiInputBase-input, & .ProseMirror, & .ProseMirror *": {
                            color: theme.palette.primary.dark,
                            WebkitTextFillColor: !editor.isEditable ? theme.palette.primary.dark : "inherit",
                        },
                        // Disabled (view mode) inputs default to MUI's grey -webkit-text-fill-color, which wins over `color`
                        "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: theme.palette.primary.dark,
                        },
                    })}
                >
                    <PolicyVersionDetails
                        {...editor.versionDetails}
                        onTitleChange={editor.updateVersionTitle}
                        onDescriptionChange={editor.updateVersionDescription}
                        isEditingContent={editor.editingContentId === policyDescriptionEditId}
                        onStartEditingContent={() => editor.actions.startEditingContent(policyDescriptionEditId)}
                    />

                    <DragDropContext onDragEnd={editor.handleDragEnd}>
                        <Droppable
                            droppableId="policy-sections"
                            type="SECTION"
                            renderClone={(provided, _snapshot, rubric) => {
                                const draggedSection = editor.sections[rubric.source.index];
                                return (
                                    <Box ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} sx={{ cursor: "grabbing" }}>
                                        <PolicySectionDragClone numberLabel={`${rubric.source.index + 1}`} title={draggedSection?.title ?? null} />
                                    </Box>
                                );
                            }}
                        >
                            {(provided, snapshot) => (
                                <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    sx={(theme) => ({ background: snapshot.isDraggingOver ? theme.palette.grey.A100 : "none" })}
                                >
                                    {editor.sections.map((section, index) => (
                                        <PolicySectionRow
                                            key={section.id}
                                            section={section}
                                            index={index}
                                            actions={editor.actions}
                                            isReordering={editor.isReordering}
                                            isEditable={editor.isEditable}
                                            availableControls={editor.availableControls}
                                            createdSectionId={editor.createdSectionId}
                                            createdSubsectionId={editor.createdSubsectionId}
                                            isAddingSubsection={editor.isAddingSubsection}
                                            isAddingSectionAfter={editor.isAddingSectionAfter}
                                            isAddingSectionAfterSubsection={editor.isAddingSectionAfterSubsection}
                                            editingContentId={editor.editingContentId}
                                            isVersionPreview={editor.isVersionPreview}
                                        />
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>

                    {editor.isEditable && (
                        <Box sx={styles.actionbar}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" sx={{ "& svg": { height: 18, width: 18 } }}>
                                {editor.isReordering ? (
                                    <Box display="flex" justifyContent="flex-end" width="100%">
                                        <DotLegalButton buttonType="primary" onClick={() => editor.setIsReordering(false)}>
                                            {translateString("doneReordering")}
                                        </DotLegalButton>
                                    </Box>
                                ) : (
                                    <>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {canDiscard && (
                                                <DotLegalTooltip text={translateString("discardDraft")}>
                                                    <IconButton size="small" onClick={onDiscardClick} disabled={isDiscardingDraft}>
                                                        {trashcan}
                                                    </IconButton>
                                                </DotLegalTooltip>
                                            )}
                                            {canCompare && (
                                                <DotLegalTooltip text={translateString("compareVersions")}>
                                                    <IconButton size="small" component={Link} to={compareUrl ?? ""}>
                                                        {showDiffIcon}
                                                    </IconButton>
                                                </DotLegalTooltip>
                                            )}
                                            {canImport && (
                                                <DotLegalTooltip text={translateString("importFromDocument")}>
                                                    <IconButton size="small" onClick={onImportClick}>
                                                        {importIcon}
                                                    </IconButton>
                                                </DotLegalTooltip>
                                            )}
                                            {canPublish && (
                                                <DotLegalTooltip text={translateString("publishNewVersion")}>
                                                    <IconButton size="small" onClick={onPublishClick}>
                                                        {publishIcon}
                                                    </IconButton>
                                                </DotLegalTooltip>
                                            )}
                                        </Box>
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            {editor.sections.length > 1 && (
                                                <DotLegalTooltip text={translateString("reorderSections")}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            editor.actions.stopEditing();
                                                            editor.setIsReordering(true);
                                                        }}
                                                    >
                                                        {reorderSectionsIcon}
                                                    </IconButton>
                                                </DotLegalTooltip>
                                            )}
                                            <DotLegalTooltip text={translateString("addSection")}>
                                                <IconButton size="small" onClick={editor.addSection} disabled={editor.isAddingSection}>
                                                    {addSectionIcon}
                                                </IconButton>
                                            </DotLegalTooltip>
                                        </Box>
                                    </>
                                )}
                            </Box>
                            {!editor.isReordering && editor.isDraftOfPublishedVersion && (
                                <Box
                                    sx={(theme) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        color: theme.palette.grey[400],
                                        fontSize: theme.typography.pxToRem(12),
                                        mt: 1,
                                    })}
                                >
                                    <Box>{translateString("draftOfPublishedBanner", { version: editor.publishedVersionLabel ?? "" })}</Box>
                                </Box>
                            )}
                        </Box>
                    )}

                    {!editor.isEditable && canStartDraft && (
                        <Box sx={styles.actionbar}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                                <Box
                                    sx={(theme) => ({
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        color: theme.palette.primary.dark,
                                        fontSize: theme.typography.body2.fontSize,
                                    })}
                                >
                                    <InfoOutlinedIcon fontSize="small" color="info" />
                                    <Box>{translateString("startDraftHint")}</Box>
                                </Box>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    <DotLegalButton buttonType="primary" onClick={onStartDraftClick} isLoading={isStartingDraft}>
                                        {translateString("startDraft")}
                                    </DotLegalButton>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {editor.sectionForDeletion && (
                        <DotLegalDeleteDialogWrapper
                            open
                            onDialogClose={editor.cancelDeleteSection}
                            onOkClick={editor.confirmDeleteSection}
                            itemForDeletion={editor.sectionForDeletion.section.title ?? ""}
                            additionalWarning={
                                editor.sectionForDeletion.section.subsections.length > 0 ? translateString("deleteSectionSubsectionsInfo") : undefined
                            }
                        >
                            {editor.sectionForDeletion.section.subsections.length > 0 && (
                                <Box sx={{ marginTop: 2 }}>
                                    <DotLegalCheckbox
                                        checked={editor.sectionForDeletion.deleteSubsections}
                                        onChange={editor.setDeleteSubsectionsChecked}
                                        label={translateString("deleteSectionAlsoDeleteSubsections")}
                                    />
                                </Box>
                            )}
                        </DotLegalDeleteDialogWrapper>
                    )}

                    {editor.subsectionForDeletion && (
                        <DotLegalDeleteDialogWrapper
                            open
                            onDialogClose={() => editor.setSubsectionForDeletion(undefined)}
                            onOkClick={editor.confirmDeleteSubsection}
                            itemForDeletion={editor.subsectionForDeletion.subsection.title ?? ""}
                        />
                    )}
                </DotLegalPaper>
            </Box>
        </Box>
    );
}

export default PolicySectionsEditor;
